/**
 * Shared accordion behavior for `details[data-accordion]` rows (Product details, FAQ).
 *
 * Reimplements the reference's accordion element on a native `<details>`: the summary
 * click is intercepted and the open/close is animated — open grows the row from summary
 * height to its full height over 0.25s while the content fades up into place over the
 * final stretch; close fades the content out while the row shrinks back, and only then
 * is `[open]` removed, because removing it first would snap the content away before the
 * height had anywhere to animate. `aria-expanded` lives on the `<details>` itself,
 * matching the reference's markup, and is what the toggle-icon CSS keys off — never
 * `[open]`, which during the closing animation is still present.
 */
(function () {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  document.querySelectorAll('details[data-accordion]').forEach((details) => {
    /* Two sections on the product page each ask for this file, and a second <script>
       tag with the same src runs the file a second time. Without this marker every row
       would carry two click handlers: the first would open it, the second would see the
       state the first had just written and close it again. */
    if (details.dataset.accordionReady != null) return;
    details.dataset.accordionReady = '';

    const summary = details.querySelector('summary');
    const content = details.querySelector('[data-accordion-content]');
    if (!summary || !content) return;

    details.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    let heightAnimation = null;

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      if (heightAnimation) heightAnimation.cancel();
      if (details.getAttribute('aria-expanded') === 'true') close();
      else open();
    });

    function open() {
      details.open = true;
      details.setAttribute('aria-expanded', 'true');
      if (reduced.matches) return;

      /* Measured after [open] so scrollHeight includes the content. */
      const from = summary.offsetHeight;
      const to = details.scrollHeight;
      details.style.overflow = 'hidden';
      heightAnimation = details.animate(
        { height: [from + 'px', to + 'px'] },
        { duration: 250, easing: 'ease' }
      );
      /* The reference starts the content fade 0.1s before the height lands. */
      content.animate(
        { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] },
        { duration: 150, delay: 150, easing: 'ease', fill: 'backwards' }
      );
      heightAnimation.onfinish = () => {
        details.style.overflow = '';
        heightAnimation = null;
      };
    }

    function close() {
      details.setAttribute('aria-expanded', 'false');
      if (reduced.matches) {
        details.open = false;
        return;
      }

      const from = details.offsetHeight;
      const to = summary.offsetHeight;
      details.style.overflow = 'hidden';
      content.animate({ opacity: [1, 0] }, { duration: 150, easing: 'ease', fill: 'forwards' });
      heightAnimation = details.animate(
        { height: [from + 'px', to + 'px'] },
        { duration: 250, easing: 'ease' }
      );
      heightAnimation.onfinish = () => {
        details.open = false;
        details.style.overflow = '';
        /* The fill:forwards opacity would leave the content invisible on reopen. */
        content.getAnimations().forEach((a) => a.cancel());
        heightAnimation = null;
      };
    }
  });
})();
